import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { alertActions } from '../redux/AlertController';
import useLogout from './useLogout';

export const useRepairShops = () => {
    const [shops, setShops] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const user = useSelector((state) => state.userController.user);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { logout } = useLogout();

    const ensureAccess = () => {
        if (!user) {
            navigate('/login');
            return false;
        }
        if (!user.access.includes('settings') && !user.access.includes('owner')) {
            navigate('/');
            return false;
        }
        return true;
    };

    const fetchShops = async () => {
        if (!ensureAccess()) return;
        setIsLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/repair-shops`, {
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `bearer ${user?.token}`,
                },
            });
            const json = await res.json();
            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json?.error || 'تعذر جلب الورش', type: 'error' }));
            } else {
                setShops(json || []);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsLoading(false);
    };

    const saveShop = async (payload, editingId = null) => {
        if (!ensureAccess()) return false;
        setSaving(true);
        try {
            const method = editingId ? 'PATCH' : 'POST';
            const url = editingId
                ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/repair-shops/${editingId}`
                : `${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/repair-shops`;
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `bearer ${user?.token}`,
                },
                body: JSON.stringify(payload),
            });
            const json = await res.json();
            if (res.status === 401) {
                logout();
                return false;
            }
            if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json?.error || 'تعذر حفظ الورشة', type: 'error' }));
                return false;
            }
            dispatch(alertActions.showAlert({ msg: editingId ? 'تم تحديث الورشة' : 'تمت إضافة الورشة', type: 'success' }));
            setShops((prev) => {
                if (editingId) {
                    return prev.map((shop) => (shop._id === editingId ? json : shop));
                }
                return [json, ...prev];
            });
            return true;
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const deleteShop = async (id) => {
        if (!ensureAccess()) return false;
        setSaving(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/repair-shops/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `bearer ${user?.token}`,
                },
            });
            const json = await res.json();
            if (res.status === 401) {
                logout();
                return false;
            }
            if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json?.error || 'تعذر حذف الورشة', type: 'error' }));
                return false;
            }
            dispatch(alertActions.showAlert({ msg: 'تم حذف الورشة', type: 'success' }));
            setShops((prev) => prev.filter((shop) => shop._id !== id));
            return true;
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
            return false;
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchShops();
        }
    }, [user]);

    return {
        shops,
        isLoading,
        saving,
        fetchShops,
        saveShop,
        deleteShop,
    };
};
